// frontend/app/components/lessonPlans/teachsense/TeachSenseMisconceptions.js
//
// The wrong ideas the class is actually holding, ranked by how often they turn
// up — the shortest path from "they did badly on this concept" to a sentence a
// teacher can open the next lesson with.
//
// The server ranks and caps the list. The rank number is drawn so the order
// reads as the server's finding rather than as an accident of layout.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import { Pill } from "../blocks/LessonBlockShell";
import { Section } from "./TeachSenseSection";
import { countLabel } from "./teachsenseText";

const MAX_CONCEPT_PILLS = 3; // then "+N", as the class-insights weak pills do

const MisconceptionRow = ({ entry, rank, colors, divided, t }) => {
  const phrase = String(entry?.phrase || "").trim();
  const count = Math.max(0, Number(entry?.count) || 0);
  const students = Math.max(0, Number(entry?.student_count) || 0);
  const conceptKeys = Array.isArray(entry?.concept_keys) ? entry.concept_keys : [];
  const shown = conceptKeys.slice(0, MAX_CONCEPT_PILLS);
  const hidden = conceptKeys.length - shown.length;

  const sample = `${countLabel(count, "mention", "mentions", t)} · ${countLabel(
    students,
    "student",
    "students",
    t
  )}`;

  return (
    <View
      accessibilityLabel={`${phrase}. ${sample}`}
      style={[styles.row, divided ? { borderTopWidth: 1, borderTopColor: colors.border } : null]}
    >
      <Text style={[styles.rank, { color: colors.textMuted }]}>{String(rank)}</Text>
      <View style={styles.body}>
        <Text style={[styles.phrase, { color: colors.textPrimary }]}>{phrase}</Text>
        <Text style={[styles.sample, { color: colors.textMuted }]}>{sample}</Text>
        {shown.length ? (
          <View style={styles.pills}>
            {shown.map((key) => (
              <Pill
                key={String(key)}
                colors={colors}
                label={String(key)}
                accent={colors.textSecondary}
                background={colors.subtleBg}
              />
            ))}
            {hidden > 0 ? (
              <Pill
                colors={colors}
                label={`+${hidden}`}
                accent={colors.textSecondary}
                background="transparent"
              />
            ) : null}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const TeachSenseMisconceptions = ({
  misconceptions = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);
  // A blank phrase is nothing a teacher can reteach, so it is dropped rather
  // than drawn as a numbered empty line.
  const entries = (Array.isArray(misconceptions) ? misconceptions : []).filter((entry) =>
    String(entry?.phrase || "").trim()
  );

  return (
    <Section
      testID="teachsense-misconceptions"
      colors={colors}
      isNarrow={isNarrow}
      icon="comment-alert-outline"
      accent={colors.danger}
      title={translate("Common misconceptions")}
      count={entries.length || null}
      isEmpty={entries.length === 0}
      empty={translate("No repeated misconceptions have surfaced yet.")}
    >
      {entries.map((entry, index) => (
        <MisconceptionRow
          key={`${entry.phrase}-${index}`}
          entry={entry}
          rank={index + 1}
          colors={colors}
          divided={index > 0}
          t={translate}
        />
      ))}
    </Section>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: "row", paddingVertical: 10 },
  rank: {
    width: 22,
    fontSize: 12,
    fontWeight: "800",
    lineHeight: 20,
    fontVariant: ["tabular-nums"],
  },
  body: { flex: 1, minWidth: 0 },
  phrase: { fontSize: 14, fontWeight: "600", lineHeight: 20, maxWidth: 720 },
  sample: { fontSize: 11, fontWeight: "500", lineHeight: 15, marginTop: 2 },
  pills: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 6 },
});

export default TeachSenseMisconceptions;
